<template>
    <nav class="px-sm mt-md flex flex-col">
        <section v-if="user && usersOrganization" name="root-pages" class="gap-sm flex flex-col">
            <SideMenuDropdown
                v-for="menuRoute in menuRoutes"
                :key="menuRoute.title"
                :category="menuRoute.title"
                :icon="menuRoute.icon"
                :links="
                    menuRoute.children !== undefined && menuRoute.children.length > 0
                        ? menuRoute.children
                        : [{ title: menuRoute.title, to: menuRoute.to ?? menuRoute.title }]
                "
            />
        </section>
    </nav>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useOrganizationStore } from '@/stores/organization';

import SideMenuDropdown from './misc/side-menu-dropdown.vue';
import { routes } from '@/router'; // use the exported array
import type { RouteMeta } from 'vue-router';

const organizationStore = useOrganizationStore();
const usersOrganization = computed(() => organizationStore.usersOrganization);
const user = computed(() => organizationStore.user);
const route = useRoute();

onMounted(() => {
    if (usersOrganization.value !== undefined && user.value !== undefined) {
        setup();
    }
});

function setup() {
    const linksToManipulateCssClasses = document.querySelectorAll('.router-link');

    linksToManipulateCssClasses.forEach((link) => {
        link.addEventListener('mouseenter', () => {
            link.classList.remove('move-up', 'stay-down'); // Ensure classes are removed before adding move-down
            link.classList.add('move-down');
        });

        link.addEventListener('mouseleave', () => {
            link.classList.remove('move-down', 'stay-down'); // Ensure classes are removed before adding move-up
            link.classList.add('move-up');
        });

        link.addEventListener('transitionend', (event) => {
            if (event.type === 'transform') {
                if (link.classList.contains('move-down')) {
                    link.classList.remove('move-down');
                    link.classList.add('stay-down');
                } else if (link.classList.contains('move-up')) {
                    link.classList.remove('move-up');
                }
            }
        });
    });
}

function hasAccess(meta: RouteMeta) {
    let hasAccess = false;

    if (meta?.requiresSomeDataAccess && organizationStore.hasDataAccess) {
        hasAccess = true;
    } else if (meta.allowedRoles && userHasRole(meta.allowedRoles)) {
        hasAccess = true;
    } else if (meta.allowedRoles == undefined && meta.requiresSomeDataAccess == undefined) {
        hasAccess = true;
    }

    return hasAccess;
}

function userHasRole(requiredRoles?: string[]) {
    if (!requiredRoles || requiredRoles.length === 0) return true; // no restriction
    if (!user.value || !usersOrganization.value) return false;

    if (organizationStore.isOrgAdmin) {
        return requiredRoles.some((r) => organizationStore.currentOrgRoles.map((role) => role.name).includes(r));
    } else {
        return requiredRoles.some((r) => organizationStore.currentUserRoles.map((role) => role.name).includes(r));
    }
}

function hasMeta<T extends { meta?: unknown }>(item: T): item is T & { meta: NonNullable<T['meta']> } {
    // a helper function to make sure the item has meta defined, to help with type checking
    return item.meta !== undefined;
}

// Query keys that represent transient page state (tab selection, datetime ranges)
// rather than user-set filters. These should not carry across menu navigation.
const NON_PERSISTENT_QUERY_KEYS = ['selected', 'dateTimeFrom'] as const;

function sanitizeCarriedQuery(query: ReturnType<typeof useRoute>['query']) {
    const out = { ...query };
    for (const key of NON_PERSISTENT_QUERY_KEYS) {
        delete out[key];
    }
    return out;
}

const menuRoutes = computed(() => {
    return routes
        .filter(hasMeta)
        .filter((r) => r.meta?.icon)
        .filter((r) => {
            return hasAccess(r.meta);
        })
        .map((r) => {
            const menuChildren =
                r.children
                    ?.filter(hasMeta)
                    .filter((c) => {
                        return hasAccess(c.meta) && c.meta.menuLink && !c.path.includes(':');
                    })

                    .map((c) => {
                        const linkTo = (c.name ?? c.path) as string;
                        // If we are currently on a route that is part of this section, we want to keep the query parameters
                        // This allows us to keep filters when clicking the menu link or navigating back from details
                        const query =
                            route.name === linkTo || (route.path.startsWith(r.path) && r.path !== '/')
                                ? sanitizeCarriedQuery(route.query)
                                : undefined;

                        return {
                            title: c.meta.title as string,
                            to: linkTo,
                            query: query,
                        };
                    }) ?? [];

            const linkTo = (r.name ?? r.path) as string;
            const query =
                route.name === linkTo || (route.path.startsWith(r.path) && r.path !== '/')
                    ? sanitizeCarriedQuery(route.query)
                    : undefined;

            if (menuChildren.length == 0 && !r.meta.shouldShowAloneInMenu) {
                // skip the item if this route shouldn't be displayed without access to children
                return undefined;
            }

            return {
                title: r.meta?.title as string,
                to: linkTo,
                icon: r.meta?.icon as string,
                children: menuChildren,
                query: query,
            };
        })
        .filter((r) => r !== undefined);
});

// watch(menuRoutes, () => {
//     console.log('menuRoutes', menuRoutes);
// });

watch(usersOrganization, async () => {
    if (usersOrganization.value !== undefined && user.value !== undefined) {
        setup();
    }
});

watch(user, async () => {
    if (usersOrganization.value !== undefined && user.value !== undefined) {
        setup();
    }
});
</script>

<style scoped>
@reference '@/assets/styles/default.css';
.router-link {
    @apply py-xs gap-sm px-sm flex items-center rounded-sm;
    transition: background-color 0.3s ease;

    &:hover {
        background-color: var(--surface-hover);
    }

    & > i {
        /* TODO: update icon instead of this */
        filter: brightness(0);
    }
}

/* ITDEVELOP-3090: the 2613-era dark invert is gone — mono icons are
   currentColor masks on the icon tokens, two-tone icons load committed
   dark variants via the .dark overrides in default.css. */

.route-active {
    background-color: var(--surface-hover);
}
</style>
